import { useNavigate } from "react-router-dom";
import { useProviderStore } from "@/stores/providerStore";
import { api } from "@/lib/api";
import { HardDrive, Shield, Lock, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const navigate = useNavigate();
  const { connectProvider, isConnecting } = useProviderStore();

  const handleConnect = async (provider: "gmail" | "outlook") => {
    try {
      await api.onboardingComplete(provider);
      await connectProvider(provider);
    } catch (err) {
      console.error(err);
    }
  };

  const skip = async () => {
    await api.onboardingComplete();
    navigate("/library");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 dark:bg-dark-surface-0 px-4">
      <div className="w-full max-w-lg rounded-2xl border border-border-default bg-surface-0 p-8 shadow-md dark:border-dark-border-default dark:bg-dark-surface-1">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-accent text-white shadow-sm">
            <HardDrive size={22} strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-primary dark:text-white">AttachDrive</h1>
        </div>

        <h2 className="text-xl font-semibold text-text-primary dark:text-dark-text-primary text-center">Welcome</h2>
        <p className="mt-2 text-sm text-text-muted text-center">
          Your email attachments stay on this device. We never store them on our servers.
        </p>

        <div className="mt-6 rounded-xl bg-surface-1 p-5 text-sm dark:bg-dark-surface-2 border border-border-subtle dark:border-dark-border-subtle">
          <p className="font-semibold text-text-primary dark:text-dark-text-primary mb-3">What happens next?</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <div className="p-1 rounded-md bg-accent-50 text-accent-600 dark:bg-accent-500/10 dark:text-accent-400 mt-0.5">
                <Shield size={14} />
              </div>
              <span className="text-text-secondary dark:text-dark-text-secondary">Connect your Gmail or Outlook account with read-only access</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="p-1 rounded-md bg-info-50 text-info-600 dark:bg-info-500/10 dark:text-info-400 mt-0.5">
                <ArrowRight size={14} />
              </div>
              <span className="text-text-secondary dark:text-dark-text-secondary">We scan for attachments and store them locally in your browser</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="p-1 rounded-md bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400 mt-0.5">
                <Lock size={14} />
              </div>
              <span className="text-text-secondary dark:text-dark-text-secondary">All data stays on your device — nothing is uploaded to our servers</span>
            </li>
          </ul>
        </div>

        <div className="mt-6 space-y-2.5">
          <button
            onClick={() => handleConnect("gmail")}
            disabled={isConnecting}
            className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-border-strong bg-surface-0 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-1 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-secondary dark:hover:bg-dark-surface-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Connect Gmail
          </button>
          <button
            onClick={() => handleConnect("outlook")}
            disabled={isConnecting}
            className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-border-strong bg-surface-0 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-1 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-secondary dark:hover:bg-dark-surface-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0078d4"><path d="M21.17 3.25Q21.5 3.25 21.76 3.5 22 3.74 22 4.08V19.92Q22 20.26 21.76 20.5 21.5 20.75 21.17 20.75H7.83Q7.5 20.75 7.24 20.5 7 20.26 7 19.92V17H2.83Q2.5 17 2.24 16.76 2 16.5 2 16.17V7.83Q2 7.5 2.24 7.24 2.5 7 2.83 7H7V4.08Q7 3.74 7.24 3.5 7.5 3.25 7.83 3.25M7 13.06L8.18 15.28H9.97L8 12.06L9.93 8.89H8.22L7.13 10.9L7.09 10.96L7.06 11.03Q6.8 10.5 6.5 9.96 6.25 9.43 5.97 8.89H4.16L6.05 12.08L4 15.28H5.78M13.88 19.5V17H8.25V19.5M13.88 15.75V12.63H12V15.75M13.88 11.38V8.25H12V11.38M13.88 7V4.5H8.25V7M20.75 19.5V17H15.13V19.5M20.75 15.75V12.63H15.13V15.75M20.75 11.38V8.25H15.13V11.38M20.75 7V4.5H15.13V7Z"/></svg>
            Connect Outlook
          </button>
        </div>

        <button
          onClick={skip}
          className="mt-5 w-full text-center text-sm text-text-muted hover:text-text-secondary transition-colors dark:text-dark-text-muted dark:hover:text-dark-text-secondary"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
